/**
 * ServiceRequest + EpisodeOfCare schemas — tracker, intake, referral/transfer.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type { PaymentTypeCode as FhirPaymentType } from '@unify/fhir-model/resources/episode-of-care';
import type {
  TrackerStage as FhirTrackerStage,
  SessionFormatPreference as FhirSessionFormat,
} from '@unify/fhir-model/resources/service-request';

export const serviceRequestIdSchema = z.string().min(1, 'Service request id is required');

/** Tracker stage codes — synced with fhir-model. */
export const trackerStageValues = [
  'new-client',
  'needs-verification',
  'insurance-eligibility',
  'waitlist',
  'screening',
  'matching',
  'consultation',
  'fulfilled',
  'transfer',
  'inactive',
  'cancelled',
] as const satisfies readonly FhirTrackerStage[];

/** Payment type codes — synced with fhir-model. */
export const paymentTypeValues = [
  'insurance',
  'self-pay',
] as const satisfies readonly FhirPaymentType[];

/** Session format preferences — synced with fhir-model. */
export const sessionFormatValues = [
  'in-person',
  'virtual',
  'hybrid',
  'no-preference',
] as const satisfies readonly FhirSessionFormat[];

/**
 * Create ServiceRequest + EpisodeOfCare pair.
 */
export const createServiceRequestSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  therapyTypeCode: z.string().min(1, 'Therapy type is required'),
  stage: z.enum(trackerStageValues).optional(),
  requesterId: z.string().optional(),
  requesterType: z.enum(['Patient', 'Practitioner', 'Organization']).optional(),
  managingOrganizationId: z.string().optional(),
  initialNote: z.string().optional(),
  authoredOn: z.string().optional(),
});

export type CreateServiceRequestInput = z.infer<typeof createServiceRequestSchema>;

/**
 * Stage transition — move a ServiceRequest through the tracker pipeline.
 */
export const transitionStageSchema = z.object({
  serviceRequestId: z.string().min(1),
  episodeOfCareId: z.string().min(1),
  newStage: z.enum(trackerStageValues),
  note: z.string().optional(),
  careManagerId: z.string().optional(),
});

export type TransitionStageInput = z.infer<typeof transitionStageSchema>;

/**
 * Client portal appointment request (ServiceRequest from patient).
 */
export const clientAppointmentRequestSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  performerOrganizationId: z.string().optional(),
  serviceTypeDisplay: z.string().optional(),
  notes: z.string().optional(),
  episodeOfCareId: z.string().optional(),
});

export type ClientAppointmentRequestInput = z.infer<typeof clientAppointmentRequestSchema>;

/**
 * Intake service request preferences.
 */
export const intakePreferencesSchema = z.object({
  sessionFormat: z.enum(sessionFormatValues).optional(),
  availabilityPreference: z.string().optional(),
  areasOfFocus: z.array(z.string()).optional(),
  providerPreferences: z.string().optional(),
  paymentType: z.enum(paymentTypeValues).optional(),
});

export type IntakePreferencesInput = z.infer<typeof intakePreferencesSchema>;
